/**
 * Marketing Terminal — Pipeline (Kanban + Composer)
 *
 * Phase 1 operational core. 7-column Kanban with status-transition buttons
 * per card and side-drawer Composer for create/edit. Drag-drop deferred to
 * Phase 1.5 polish (status buttons are more predictable + accessible).
 *
 * Backend:
 *   GET    /api/admin/marketing/social/kanban
 *   POST   /api/admin/marketing/social
 *   PUT    /api/admin/marketing/social/:id
 *   POST   /api/admin/marketing/social/:id/transition
 *   DELETE /api/admin/marketing/social/:id
 *   GET    /api/admin/marketing/connections
 *   GET    /api/admin/marketing/brand-kit/active
 */

import React, { useState } from 'react';
import {
  AlertTriangle,
  Calendar,
  Check,
  CheckCircle2,
  Clock,
  Edit3,
  Megaphone,
  Plus,
  Send,
  Trash2,
  X,
  ArrowRight,
  Activity,
} from 'lucide-react';
import { QueryClient, QueryClientProvider, useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { adminFetch } from '../../../utils/adminApi';

// ── Types ────────────────────────────────────────────────────────────────────

interface SocialPost {
  id: string;
  status: string;
  platforms: string[];
  posting_account: string;
  platform_connection_id: string | null;
  content_text: string;
  content_media_urls: string[];
  hashtags: string[];
  cta: string | null;
  cta_url: string | null;
  primary_persona_id: string | null;
  pillar_tags: string[];
  scheduled_at: string | null;
  published_at: string | null;
  deployment_strength: number | null;
  drafted_by_agent: boolean;
  drafted_by: string | null;
  approved_by: string | null;
  publish_attempt_count: number;
  last_publish_error: string | null;
  created_at: string;
  updated_at: string;
}

interface KanbanResponse {
  data: Record<string, SocialPost[]>;
  totals: Record<string, number>;
}

interface BrandKit {
  pillars: Array<{ key: string; name: string }>;
  locked_phrases: Array<{ key: string; text: string }>;
}

interface Connection {
  id: string;
  platform: string;
  account_handle: string | null;
  account_label: string | null;
  account_type: string;
  is_default: boolean;
  is_connected: boolean;
}

const queryClient = new QueryClient({ defaultOptions: { queries: { staleTime: 15_000 } } });

export function PipelinePage() {
  return (
    <QueryClientProvider client={queryClient}>
      <PipelineInner />
    </QueryClientProvider>
  );
}

// ── Constants ────────────────────────────────────────────────────────────────

const STATUS_COLUMNS: Array<{
  key: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  ringColor?: string;
  description: string;
}> = [
  { key: 'draft',             label: 'Draft',             icon: Megaphone,   description: 'Work in progress' },
  { key: 'ready_for_review',  label: 'Ready for Review',  icon: Clock,       ringColor: 'ring-amber-300', description: 'Awaiting your approval' },
  { key: 'approved',          label: 'Approved',          icon: CheckCircle2, description: 'Approved, not yet scheduled' },
  { key: 'scheduled',         label: 'Scheduled',         icon: Calendar,    description: 'Queued for publish' },
  { key: 'published',         label: 'Published',         icon: Send,        description: 'Live on platform' },
  { key: 'publish_failed',    label: 'Failed',            icon: AlertTriangle, ringColor: 'ring-red-300', description: 'Needs attention' },
  { key: 'archived',          label: 'Archived',          icon: Activity,    description: 'Removed from active flow' },
];

// Allowed forward transitions per status. Backward "draft" always allowed.
const ALLOWED_TRANSITIONS: Record<string, string[]> = {
  draft:             ['ready_for_review', 'archived'],
  ready_for_review:  ['draft', 'approved', 'archived'],
  approved:          ['draft', 'scheduled', 'archived'],
  scheduled:         ['approved', 'archived'],
  published:         ['archived'],
  publish_failed:    ['draft', 'archived'],
  archived:          ['draft'],
};

const PLATFORMS = ['linkedin', 'x', 'facebook', 'instagram', 'tiktok', 'reddit', 'youtube'] as const;

// ── Inner ────────────────────────────────────────────────────────────────────

function PipelineInner() {
  const [composer, setComposer] = useState<{ mode: 'create' | 'edit'; post: SocialPost | null } | null>(null);
  const [filterPlatform, setFilterPlatform] = useState<string>('');

  const kanban = useQuery<KanbanResponse>({
    queryKey: ['marketing-pipeline', filterPlatform],
    queryFn: () => {
      const qs = filterPlatform ? `?platform=${filterPlatform}` : '';
      return adminFetch<KanbanResponse>(`/api/admin/marketing/social/kanban${qs}`);
    },
    refetchInterval: 30_000,
  });

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold" style={{ fontFamily: "'League Spartan', sans-serif", color: 'var(--cedar-ink)' }}>
            Pipeline
          </h1>
          <p className="mt-1 text-sm text-gray-500">
            Social posts moving from draft to published. {kanban.data?.totals && (
              <span>
                {Object.values(kanban.data.totals).reduce((s, n) => s + n, 0)} total
                {' · '}
                {kanban.data.totals.ready_for_review || 0} awaiting review
              </span>
            )}
          </p>
        </div>

        <div className="flex items-center gap-2">
          <select
            value={filterPlatform}
            onChange={e => setFilterPlatform(e.target.value)}
            className="rounded border border-gray-200 px-2 py-1.5 text-xs"
          >
            <option value="">All platforms</option>
            {PLATFORMS.map(p => <option key={p} value={p}>{p}</option>)}
          </select>
          <button
            onClick={() => setComposer({ mode: 'create', post: null })}
            className="flex items-center gap-1 rounded px-3 py-1.5 text-xs font-medium text-white"
            style={{ backgroundColor: 'var(--cedar-primary)' }}
          >
            <Plus className="h-3 w-3" /> New post
          </button>
        </div>
      </div>

      {/* Kanban */}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-7">
        {STATUS_COLUMNS.map(col => {
          const Icon = col.icon;
          const items = kanban.data?.data?.[col.key] ?? [];
          const total = kanban.data?.totals?.[col.key] ?? 0;

          return (
            <div
              key={col.key}
              className={`flex flex-col rounded-lg bg-white p-3 ${col.ringColor && total > 0 ? `ring-2 ${col.ringColor}` : ''}`}
              style={{ boxShadow: '0px 1px 3px rgba(0,0,0,0.1), 0px 1px 2px rgba(0,0,0,0.06)', minHeight: '200px' }}
            >
              <div className="mb-2 flex items-start justify-between">
                <div className="flex items-center gap-1.5">
                  <Icon className="h-3.5 w-3.5 text-gray-400" />
                  <span className="text-[11px] font-semibold uppercase tracking-wide text-gray-600">
                    {col.label}
                  </span>
                </div>
                <span className="text-xs font-bold text-gray-800">{total}</span>
              </div>

              <div className="flex-1 space-y-2 overflow-y-auto" style={{ maxHeight: '70vh' }}>
                {items.map(post => (
                  <PostCard
                    key={post.id}
                    post={post}
                    onEdit={() => setComposer({ mode: 'edit', post })}
                  />
                ))}
                {items.length === 0 && (
                  <div className="text-center text-[11px] italic text-gray-300 py-4">
                    {col.description}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Composer drawer */}
      {composer && (
        <ComposerDrawer
          mode={composer.mode}
          post={composer.post}
          onClose={() => setComposer(null)}
        />
      )}
    </div>
  );
}

// ── Card ─────────────────────────────────────────────────────────────────────

function PostCard({ post, onEdit }: { post: SocialPost; onEdit: () => void }) {
  const queryClient = useQueryClient();
  const [showTransitionMenu, setShowTransitionMenu] = useState(false);

  const transition = useMutation({
    mutationFn: (target: string) =>
      adminFetch(`/api/admin/marketing/social/${post.id}/transition`, {
        method: 'POST',
        body: JSON.stringify({ status: target }),
        headers: { 'Content-Type': 'application/json' },
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['marketing-pipeline'] });
      setShowTransitionMenu(false);
    },
  });

  const remove = useMutation({
    mutationFn: () =>
      adminFetch(`/api/admin/marketing/social/${post.id}`, { method: 'DELETE' }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['marketing-pipeline'] }),
  });

  const allowedTargets = ALLOWED_TRANSITIONS[post.status] ?? [];

  return (
    <div className="rounded border border-gray-100 bg-gray-50 p-2 text-xs hover:bg-gray-100">
      <div className="mb-1 flex items-start justify-between">
        <div className="flex flex-wrap gap-1">
          {post.platforms?.slice(0, 3).map(p => (
            <span key={p} className="rounded bg-white px-1 py-0.5 text-[9px] font-medium text-gray-600">
              {p}
            </span>
          ))}
          {(post.platforms?.length ?? 0) > 3 && (
            <span className="text-[9px] text-gray-400">+{post.platforms.length - 3}</span>
          )}
        </div>
        {post.drafted_by_agent && (
          <span
            className="rounded px-1 py-0.5 text-[9px] font-medium"
            style={{ backgroundColor: 'var(--cedar-subtle)', color: 'var(--cedar-primary)' }}
            title="Drafted by Cowork"
          >
            agent
          </span>
        )}
      </div>

      <button onClick={onEdit} className="block w-full text-left">
        <div className="line-clamp-3 text-[11px] text-gray-700">
          {post.content_text || <span className="italic text-gray-400">(no content)</span>}
        </div>
      </button>

      {post.last_publish_error && (
        <div className="mt-1 rounded bg-red-50 px-1.5 py-1 text-[10px] text-red-700">
          <AlertTriangle className="mr-0.5 inline h-2.5 w-2.5" />
          {post.last_publish_error}
        </div>
      )}

      {post.scheduled_at && (
        <div className="mt-1 text-[10px] text-gray-500">
          <Calendar className="mr-0.5 inline h-2.5 w-2.5" />
          {new Date(post.scheduled_at).toLocaleString()}
        </div>
      )}

      <div className="mt-1.5 flex items-center justify-between">
        <button
          onClick={onEdit}
          className="flex items-center gap-0.5 text-[10px] text-gray-500 hover:text-gray-700"
        >
          <Edit3 className="h-2.5 w-2.5" /> Edit
        </button>

        {allowedTargets.length > 0 && (
          <div className="relative">
            <button
              onClick={() => setShowTransitionMenu(!showTransitionMenu)}
              className="flex items-center gap-0.5 rounded px-1.5 py-0.5 text-[10px] font-medium hover:bg-white"
              style={{ color: 'var(--cedar-primary)' }}
              disabled={transition.isPending}
            >
              {transition.isPending ? '...' : <>Move <ArrowRight className="h-2.5 w-2.5" /></>}
            </button>

            {showTransitionMenu && (
              <div
                className="absolute right-0 z-10 mt-1 w-40 rounded-lg bg-white p-1"
                style={{ boxShadow: '0px 4px 12px rgba(0,0,0,0.1)' }}
              >
                {allowedTargets.map(target => {
                  // Scheduled requires a datetime — show special UI for that
                  if (target === 'scheduled') {
                    return (
                      <ScheduleButton
                        key={target}
                        postId={post.id}
                        onSchedule={dt => transition.mutate(target)}
                      />
                    );
                  }
                  return (
                    <button
                      key={target}
                      onClick={() => transition.mutate(target)}
                      className="block w-full rounded px-2 py-1 text-left text-[10px] text-gray-700 hover:bg-gray-50"
                    >
                      → {STATUS_COLUMNS.find(c => c.key === target)?.label ?? target}
                    </button>
                  );
                })}
                {(post.status === 'draft' || post.status === 'archived') && (
                  <>
                    <div className="my-1 border-t border-gray-100" />
                    <button
                      onClick={() => {
                        if (confirm('Delete this draft?')) remove.mutate();
                      }}
                      className="block w-full rounded px-2 py-1 text-left text-[10px] text-red-600 hover:bg-red-50"
                    >
                      <Trash2 className="mr-1 inline h-2.5 w-2.5" /> Delete
                    </button>
                  </>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// Note: post.id passed but unused — schedule still uses parent transition mutation.
// onSchedule kept for future precise timestamp-passing flow.
function ScheduleButton({ postId, onSchedule }: { postId: string; onSchedule: (dt: string) => void }) {
  const queryClient = useQueryClient();
  const [showInput, setShowInput] = useState(false);
  const [datetime, setDatetime] = useState('');

  const schedule = useMutation({
    mutationFn: (dt: string) =>
      adminFetch(`/api/admin/marketing/social/${postId}/transition`, {
        method: 'POST',
        body: JSON.stringify({ status: 'scheduled', scheduled_at: new Date(dt).toISOString() }),
        headers: { 'Content-Type': 'application/json' },
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['marketing-pipeline'] }),
  });

  if (!showInput) {
    return (
      <button
        onClick={() => setShowInput(true)}
        className="block w-full rounded px-2 py-1 text-left text-[10px] text-gray-700 hover:bg-gray-50"
      >
        → Scheduled (set time…)
      </button>
    );
  }

  return (
    <div className="p-1">
      <input
        type="datetime-local"
        value={datetime}
        onChange={e => setDatetime(e.target.value)}
        className="w-full rounded border border-gray-200 px-1 py-0.5 text-[10px]"
      />
      <button
        onClick={() => {
          if (datetime) schedule.mutate(datetime);
        }}
        disabled={!datetime || schedule.isPending}
        className="mt-1 block w-full rounded px-2 py-1 text-[10px] font-medium text-white disabled:opacity-50"
        style={{ backgroundColor: 'var(--cedar-primary)' }}
      >
        {schedule.isPending ? 'Scheduling...' : 'Schedule'}
      </button>
    </div>
  );
}

// ── Composer drawer ──────────────────────────────────────────────────────────

function ComposerDrawer({
  mode,
  post,
  onClose,
}: {
  mode: 'create' | 'edit';
  post: SocialPost | null;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();

  const [platforms, setPlatforms] = useState<string[]>(post?.platforms ?? ['linkedin']);
  const [postingAccount, setPostingAccount] = useState<string>(post?.posting_account ?? 'brand');
  const [contentText, setContentText] = useState(post?.content_text ?? '');
  const [hashtagsRaw, setHashtagsRaw] = useState((post?.hashtags ?? []).join(' '));
  const [cta, setCta] = useState(post?.cta ?? '');
  const [ctaUrl, setCtaUrl] = useState(post?.cta_url ?? '');
  const [pillarTags, setPillarTags] = useState<string[]>(post?.pillar_tags ?? []);

  const brand = useQuery<{ data: BrandKit }>({
    queryKey: ['marketing-brand-kit'],
    queryFn: () => adminFetch('/api/admin/marketing/brand-kit/active'),
    staleTime: 5 * 60_000,
  });

  const submit = useMutation({
    mutationFn: () => {
      const body = {
        platforms,
        posting_account: postingAccount,
        content_text: contentText,
        hashtags: hashtagsRaw.split(/\s+/).filter(t => t.startsWith('#') || (t && !t.includes(' '))),
        cta: cta || null,
        cta_url: ctaUrl || null,
        pillar_tags: pillarTags,
      };
      if (mode === 'edit' && post) {
        return adminFetch(`/api/admin/marketing/social/${post.id}`, {
          method: 'PUT',
          body: JSON.stringify(body),
          headers: { 'Content-Type': 'application/json' },
        });
      }
      return adminFetch('/api/admin/marketing/social', {
        method: 'POST',
        body: JSON.stringify(body),
        headers: { 'Content-Type': 'application/json' },
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['marketing-pipeline'] });
      onClose();
    },
  });

  // Brand-rule warnings (cheap client-side check; server-side Quality Gate is canonical)
  const violations = checkBrandRules(contentText);

  return (
    <div className="fixed inset-0 z-50 flex">
      <div className="flex-1 bg-black/30" onClick={onClose} />
      <div
        className="flex w-full max-w-2xl flex-col overflow-y-auto bg-white"
        style={{ boxShadow: '-4px 0 24px rgba(0,0,0,0.1)' }}
      >
        <div className="flex items-center justify-between border-b border-gray-100 p-4">
          <h2 className="text-lg font-bold text-gray-900">
            {mode === 'create' ? 'New social post' : 'Edit post'}
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="flex-1 space-y-4 p-4">
          {/* Platforms */}
          <Field label="Platforms">
            <div className="flex flex-wrap gap-1.5">
              {PLATFORMS.map(p => (
                <button
                  key={p}
                  onClick={() => {
                    if (platforms.includes(p)) setPlatforms(platforms.filter(x => x !== p));
                    else setPlatforms([...platforms, p]);
                  }}
                  className={`rounded px-2 py-1 text-xs font-medium ${
                    platforms.includes(p) ? 'text-white' : 'text-gray-600'
                  }`}
                  style={
                    platforms.includes(p)
                      ? { backgroundColor: 'var(--cedar-primary)' }
                      : { backgroundColor: 'var(--cedar-limestone)' }
                  }
                >
                  {p}
                </button>
              ))}
            </div>
          </Field>

          {/* Posting account */}
          <Field label="Posting as">
            <select
              value={postingAccount}
              onChange={e => setPostingAccount(e.target.value)}
              className="w-full rounded border border-gray-200 px-2 py-1.5 text-xs"
            >
              <option value="brand">Brand (Coach Cedar)</option>
              <option value="personal">Personal (Founder)</option>
              <option value="community">Community</option>
            </select>
          </Field>

          {/* Content */}
          <Field label="Content">
            <textarea
              value={contentText}
              onChange={e => setContentText(e.target.value)}
              rows={8}
              className="w-full rounded border border-gray-200 px-2 py-1.5 text-sm font-mono"
              placeholder="What does Coach Cedar want to say?"
            />
            <div className="mt-1 flex items-center justify-between text-[10px] text-gray-500">
              <span>{contentText.length} chars</span>
              {platforms.includes('linkedin') && (
                <span className={contentText.length > 1300 ? 'text-amber-600' : ''}>
                  LinkedIn sweet spot: 800-1300
                </span>
              )}
              {platforms.includes('x') && (
                <span className={contentText.length > 280 ? 'text-red-600' : ''}>
                  X limit: 280
                </span>
              )}
            </div>
            {violations.length > 0 && (
              <div className="mt-2 rounded bg-amber-50 p-2 text-[11px] text-amber-800">
                <AlertTriangle className="mr-1 inline h-3 w-3" />
                <strong>Brand rule warnings:</strong>
                <ul className="ml-4 mt-1 list-disc">
                  {violations.map((v, i) => <li key={i}>{v}</li>)}
                </ul>
              </div>
            )}
          </Field>

          {/* Hashtags */}
          <Field label="Hashtags (max 3-5 in 2026)">
            <input
              value={hashtagsRaw}
              onChange={e => setHashtagsRaw(e.target.value)}
              placeholder="#fitness #coaching"
              className="w-full rounded border border-gray-200 px-2 py-1.5 text-xs"
            />
          </Field>

          {/* CTA */}
          <div className="grid grid-cols-2 gap-3">
            <Field label="CTA text">
              <input
                value={cta}
                onChange={e => setCta(e.target.value)}
                placeholder="Try Coach Cedar"
                className="w-full rounded border border-gray-200 px-2 py-1.5 text-xs"
              />
            </Field>
            <Field label="CTA URL">
              <input
                value={ctaUrl}
                onChange={e => setCtaUrl(e.target.value)}
                placeholder="https://coachcedar.com/lp/founder-story"
                className="w-full rounded border border-gray-200 px-2 py-1.5 text-xs"
              />
            </Field>
          </div>

          {/* Pillar tags */}
          <Field label="Pillar tags">
            <div className="flex flex-wrap gap-1.5">
              {(brand.data?.data?.pillars ?? []).map(p => {
                const selected = pillarTags.includes(p.key);
                return (
                  <button
                    key={p.key}
                    onClick={() => {
                      if (selected) setPillarTags(pillarTags.filter(x => x !== p.key));
                      else setPillarTags([...pillarTags, p.key]);
                    }}
                    className={`rounded-full px-3 py-1 text-xs font-medium ${
                      selected ? 'text-white' : 'text-gray-700'
                    }`}
                    style={
                      selected ? { backgroundColor: 'var(--cedar-primary)' } : { backgroundColor: 'var(--cedar-subtle)' }
                    }
                  >
                    {p.name}
                  </button>
                );
              })}
            </div>
          </Field>
        </div>

        {mode === 'edit' && post && (
          <QualityGatePanel postId={post.id} />
        )}

        <div className="flex items-center justify-between border-t border-gray-100 bg-gray-50 p-4">
          <button onClick={onClose} className="px-3 py-1.5 text-xs text-gray-600 hover:text-gray-900">
            Cancel
          </button>
          <button
            onClick={() => submit.mutate()}
            disabled={!contentText.trim() || platforms.length === 0 || submit.isPending}
            className="rounded px-4 py-1.5 text-xs font-medium text-white disabled:opacity-50"
            style={{ backgroundColor: 'var(--cedar-primary)' }}
          >
            {submit.isPending ? 'Saving...' : mode === 'create' ? 'Save draft' : 'Update'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Quality Gate panel ──────────────────────────────────────────────────────
// Master spec §3 + §24. Click "Run Pre-Flight Assessment" → backend runs
// brand-rule mechanical check + LLM-driven per-criterion scoring → returns
// deployment_strength + violations + per-criterion fixes.

interface AssessmentResult {
  overall_score: number;
  deployment_strength: 'ship' | 'iterate' | 'reject';
  threshold: number;
  passed: boolean;
  per_criterion: Array<{
    criterion_key: string;
    title: string;
    score: number;
    severity: string;
    weight: number;
    reasoning: string;
    suggested_fix: string | null;
  }>;
  brand_rule_violations: Array<{
    rule: string;
    severity: string;
    match: string;
    explanation: string;
  }>;
  drafter_model: string;
  evaluator_model: string;
}

function QualityGatePanel({ postId }: { postId: string }) {
  const [result, setResult] = useState<AssessmentResult | null>(null);

  const assess = useMutation({
    mutationFn: () =>
      adminFetch<{ data: AssessmentResult }>('/api/admin/marketing/quality/assess', {
        method: 'POST',
        body: JSON.stringify({ content_table: 'social_posts', content_id: postId }),
        headers: { 'Content-Type': 'application/json' },
      }),
    onSuccess: data => setResult(data.data),
  });

  return (
    <div className="border-t border-gray-100 bg-gray-50 p-4">
      <div className="mb-2 flex items-center justify-between">
        <div className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">
          Quality Gate
        </div>
        <button
          onClick={() => assess.mutate()}
          disabled={assess.isPending}
          className="flex items-center gap-1 rounded px-3 py-1 text-xs font-medium text-white disabled:opacity-50"
          style={{ backgroundColor: 'var(--cedar-stream)' }}
        >
          {assess.isPending ? 'Assessing...' : 'Run Pre-Flight Assessment'}
        </button>
      </div>

      {assess.isError && (
        <div className="rounded bg-red-50 p-2 text-xs text-red-700">
          Assessment failed: {(assess.error as Error)?.message}
        </div>
      )}

      {result && (
        <div className="space-y-3">
          {/* Overall score */}
          <div className="flex items-center gap-3 rounded-lg bg-white p-3" style={{ boxShadow: '0px 1px 3px rgba(0,0,0,0.06)' }}>
            <div className="flex flex-col items-center">
              <div
                className="text-2xl font-bold"
                style={{
                  color:
                    result.deployment_strength === 'ship' ? 'var(--cedar-primary)' :
                    result.deployment_strength === 'iterate' ? 'var(--status-warning)' :
                    'var(--status-error)',
                }}
              >
                {result.overall_score}
              </div>
              <div className="text-[10px] uppercase text-gray-500">/ {result.threshold}</div>
            </div>
            <div className="flex-1">
              <div
                className="flex items-center gap-1 text-sm font-bold"
                style={{
                  color:
                    result.deployment_strength === 'ship' ? 'var(--cedar-primary)' :
                    result.deployment_strength === 'iterate' ? 'var(--status-warning)' :
                    'var(--status-error)',
                }}
              >
                {result.deployment_strength === 'ship' ? <><Check size={15} />Ship</> :
                 result.deployment_strength === 'iterate' ? <><AlertTriangle size={15} />Iterate</> :
                 <><X size={15} />Reject</>}
              </div>
              <div className="text-[10px] text-gray-500">
                Drafter: {result.drafter_model} · Evaluator: {result.evaluator_model}
              </div>
            </div>
          </div>

          {/* Brand rule violations */}
          {result.brand_rule_violations.length > 0 && (
            <div className="rounded-lg bg-red-50 p-2 text-xs">
              <div className="mb-1 font-semibold text-red-800">
                {result.brand_rule_violations.length} brand rule violation{result.brand_rule_violations.length !== 1 ? 's' : ''}
              </div>
              {result.brand_rule_violations.map((v, i) => (
                <div key={i} className="flex items-start gap-1.5 py-0.5">
                  <span
                    className="mt-0.5 rounded px-1 text-[9px] font-bold uppercase"
                    style={{
                      backgroundColor: v.severity === 'must_fix' ? 'var(--status-error)' : 'var(--status-warning)',
                      color: '#fff',
                    }}
                  >
                    {v.severity === 'must_fix' ? 'MUST' : 'SHOULD'}
                  </span>
                  <div className="flex-1">
                    <code className="rounded bg-white px-1 text-[10px] text-red-700">{v.match}</code>
                    <div className="text-[10px] text-gray-700">{v.explanation}</div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Per-criterion */}
          {result.per_criterion.length > 0 && (
            <div className="space-y-1">
              {result.per_criterion.map(c => (
                <div
                  key={c.criterion_key}
                  className="rounded border border-gray-100 bg-white p-2"
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1">
                      <div className="flex items-center gap-1.5">
                        <span className="text-xs font-semibold text-gray-900">{c.title}</span>
                        <span className="text-[9px] text-gray-400">w={c.weight}</span>
                      </div>
                      <div className="mt-0.5 text-[10px] text-gray-600">{c.reasoning}</div>
                      {c.suggested_fix && c.score < 80 && (
                        <div className="mt-1 rounded bg-amber-50 px-1.5 py-1 text-[10px] text-amber-800">
                          <strong>Fix:</strong> {c.suggested_fix}
                        </div>
                      )}
                    </div>
                    <div
                      className="flex h-7 w-9 flex-col items-center justify-center rounded text-[10px] font-bold"
                      style={{
                        backgroundColor:
                          c.score >= 80 ? 'var(--cedar-subtle)' :
                          c.score >= 50 ? 'var(--status-warning-bg)' :
                          'var(--status-error-bg)',
                        color:
                          c.score >= 80 ? 'var(--cedar-primary)' :
                          c.score >= 50 ? 'var(--status-warning-text)' :
                          'var(--status-error-text)',
                      }}
                    >
                      {c.score}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-500">
        {label}
      </label>
      {children}
    </div>
  );
}

// ── Cheap client-side brand rule pre-flight ──────────────────────────────────
// Authoritative check is server-side Quality Gate (Phase 4). This is a hint.

const FORBIDDEN_PATTERNS = [
  { pattern: /\bplatform\b/i, msg: 'Forbidden: "platform" — use "Coach Cedar" or "the relationship"' },
  { pattern: /\bsolution\b/i, msg: 'Forbidden: "solution" — use "Coach Cedar"' },
  { pattern: /\boptimize\b/i, msg: 'Forbidden: "optimize" — use "adapt" or "recalibrate"' },
  { pattern: /\bleverage\b/i, msg: 'Forbidden: "leverage" — rewrite the sentence' },
  { pattern: /\b24\/7\b/i, msg: 'Forbidden: "24/7" — use "stays with you"' },
  { pattern: /\balways on\b/i, msg: 'Forbidden: "always on" — use "stays with you"' },
  { pattern: /\bAI\b/, msg: '"AI" mention in consumer-facing content — remove or rewrite' },
  { pattern: /\balgorithm/i, msg: '"algorithm" mention — remove or rewrite' },
  { pattern: /\bmachine learning\b/i, msg: '"machine learning" — use "remembers" or "learns"' },
  { pattern: /—/, msg: 'Em dash detected — use commas or periods for rhythm' },
  { pattern: /\bunlike\b.*\b(competitor|other)\b/i, msg: '"unlike X" framing forbidden — name the status quo experience instead' },
  { pattern: /\bbetter than\b/i, msg: '"better than" framing forbidden' },
  { pattern: /(?:^|[^a-zA-Z])Cedar(?![\sa-z])/, msg: '"Cedar" alone — always use "Coach Cedar" in external copy' },
];

function checkBrandRules(text: string): string[] {
  const out: string[] = [];
  for (const rule of FORBIDDEN_PATTERNS) {
    if (rule.pattern.test(text)) out.push(rule.msg);
  }
  return out;
}

export default PipelinePage;
